<template>
  <section class="card-style-order">
    <header class="card-style-order__header">
      <!-- 单个商品 -->
      <template v-if="merchandiseList.length === 1">
        <div
          v-for="merchandise in merchandiseList"
          :key="merchandise.id"
          class="single-merchandise"
        >
          <span class="merchandise-name">
            <span class="name">
              {{ merchandise.productName }}
              <template v-if="merchandise.skuValue"
                >({{ merchandise.skuValue }})</template
              >
            </span>
            <i>x{{ merchandise.number }}</i></span
          >
          <span class="pay-amount"
            >金额 <em>¥ {{ NP.divide(Number(order.payAmount), 100) }}</em></span
          >
        </div>
      </template>
      <!-- 多个商品 -->
      <div v-else class="multiple-merchandise">
        <div
          v-for="(merchandise, index) in multipleMerchandiseList"
          :key="merchandise.id"
          class="single-merchandise"
        >
          <span class="merchandise-name"
            >{{ merchandise.productName }}
            <template v-if="merchandise.skuValue"
              >({{ merchandise.skuValue }})</template
            >
            <i>x{{ merchandise.number }}</i></span
          >
          <span
            class="expand-btn"
            v-if="
              merchandiseList.length > 2 &&
              index === multipleMerchandiseList.length - 1
            "
            @click="isExpand = !isExpand"
            >{{ isExpand ? '收起' : '展开' }}</span
          >
        </div>
        <div class="summary">
          <span
            >共<em>{{ merchandiseSpeciesNumber }}</em
            >种商品，共<em>{{ merchandiseNumber }}</em
            >件</span
          >
          <span class="pay-amount"
            >金额 <em>¥ {{ NP.divide(Number(order.payAmount), 100) }}</em></span
          >
        </div>
      </div>
    </header>
    <main class="card-style-order__main">
      <div class="description" v-for="[key, obj] in description" :key="key">
        <span class="label">{{ key }}</span>
        <span class="value"
          >{{ obj.value }}

          <span class="extra-tag" v-if="obj.showTag">补款订单</span>
          <i class="suffix">{{ obj.suffix }}</i></span
        >
        <span class="extra" @click="onExtraClick(key, obj)">{{
          obj.extra
        }}</span>
      </div>
      <!-- 根据状态判断已核销 -->
      <img
        v-if="order.verifyStatusText === '已核销'"
        class="has-verification"
        src="@/assets/images/agent/storeTab/has_verification.png"
        alt=""
      />
    </main>

    <footer class="card-style-order__footer">
      <div class="card-style-order__footer__left">
        <!-- 根据状态判断核销状态 -->
        <div
          v-if="order.verifyStatus !== VERIFY_STATUS.NONE"
          :class="['status', `status--${order.verifyStatus}`]"
        >
          <span
            >{{ order.verifyStatusText }}
            <template v-if="order.verifyTime"
              >(核销时间：{{ order.verifyTime }})</template
            ></span
          >
        </div>
      </div>

      <div class="card-style-order__footer__right">
        <div
          v-if="showDetailBtn"
          class="detail-btn"
          @click="$emit('view', order)"
        >
          查看详情
        </div>
        <div
          v-if="canVerifyAll"
          class="detail-btn verification-btn"
          @click="
            $emit('verify', 'all', order, index, '', order.remainVerifyNumber)
          "
        >
          全部核销
        </div>
      </div>
    </footer>
  </section>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import NP from 'number-precision'
import { Toast } from 'vant'
import store from '@/store'

import { copyMultipleLines } from '@/utils/utils'
// 大健康 - 门店系统相关控制器与组件
import StoreController from '@/controllers/storeController/view/store/store.controller.js'
import { AGRNT_AUTH_CODE } from '@/pages/agent/js/enum'

/** 订单核销状态：后端说可能会不准，最好用 refundStatusText */
const VERIFY_STATUS = {
  NONE: 0, // 无需核销
  UN_VERIFY: 1, // 待核销
  PARTIAL_VERIFY: 2, // 部分核销 订单中的核销码只有未核销 / 已核销
  REFUNDING: 3, // 退款中 ps: 3和6都为退款中，后端说是为了兼容旧逻辑
  ALL_VERIFY: 4, // 全部核销
  PARTIAL_OUTDATE: 5, // 部分过期 订单中有任意一个核销码为已过期的状态，但并非全部核销码都为已过期状态
  OUTDATE: 7, // 全部过期 订单中所有的核销码都为已过期
  PARTIAL_REFUND: 8, // 部分退款
  HAS_REFUND: 9, // 已退款
}

export default defineComponent({
  props: {
    index: [String, Number],
    order: {
      type: Object,
      required: true,
    },
    /** 条件显示查看详情 */
    showDetailBtn: {
      type: Boolean,
      default: true,
    },
    showPhone: {
      type: Boolean,
      default: false,
    },
    // 来源：上级页面
    from: {
      type: String,
      default: '',
    },
    showVerify: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, ctx) {
    /** 订单商品列表信息 */
    const merchandiseList = computed(() => {
      const order = props.order
      return order.detailLs || []
    })

    /** 商品数量 */
    const merchandiseNumber = computed(() => {
      return merchandiseList.value.reduce((prev, curr) => {
        return prev + curr.number
      }, 0)
    })

    /** 商品种数 */
    const merchandiseSpeciesNumber = computed(() => {
      return merchandiseList.value.length || 0
    })

    const isExpand = ref(false)

    /** 订单商品种类大于2时的展示列表 */
    const multipleMerchandiseList = computed(() => {
      if (isExpand.value) {
        return merchandiseList.value
      } else {
        return merchandiseList.value.slice(0, 2)
      }
    })

    const fillDefault = value => value || '--'

    const description = computed(() => {
      const map = new Map()
      const order = props.order
      map.set('订单ID', {
        value: order.id,
        extra: '复制',
        showTag: props.order.orderSegmentType == 1230, // 补款订单显示标签
      })
      map.set('买家', {
        value: fillDefault(order.fUserName),
        suffix: `(ID: ${fillDefault(order.fUserId)})`,
      })
      if (props.showPhone) {
        map.set('联系方式', {
          value: fillDefault(order.memberPhone || order.phone),
        })
      }
      map.set('下单时间', { value: fillDefault(order.createTime) })
      map.set('订单状态', { value: fillDefault(order.statusText) })
      map.set('跟进代理', {
        value: fillDefault(order.followName),
        suffix: `(ID: ${fillDefault(order.followId)})`,
      })

      // 退款信息
      if ([1, 2].includes(order.refundStatus)) {
        map.set('退款金额', {
          value: `¥${NP.divide(order.refundMoney, 100).toFixed(2)}`,
        })
        map.set('退款时间', { value: order.refundTime })
      }

      return map
    })

    /**
     * 点击额外按钮
     * @param {String} key 键
     * @param {Object} obj 值
     */
    const onExtraClick = (key, obj) => {
      const map = {
        订单ID: () => {
          /*
          * 订单ID：XXXX
            商品ID：商品名称（规格值）（ID：XXXXXXX）
            买家：某某某(ID：XXXXX)
            收货人：收货人姓名
            收货电话：XXXXXXXXXX
            下单时间2025-04-2912:33:31
            订单状态：已完成
            跟进代理：某某某(ID: 74743297)
          * */
          const order = props.order
          let productStr = '商品ID：'
          for (const merchandise of merchandiseList.value) {
            productStr += `${merchandise.productName}${
              !merchandise.skuValue ? '' : '(' + merchandise.skuValue + ')'
            }(ID：${merchandise.productId})、`
          }
          productStr = productStr.slice(0, -1)
          const str = `订单ID：${order.id}\n${productStr}\n买家：${
            order.fUserName
          }(ID：${order.fUserId})\n收货人：${order.name || '--'}\n收货电话：${
            order.phone || '--'
          }\n下单时间：${order.createTime}\n订单状态：${
            order.statusText
          }\n跟进代理：${order.followName}(ID: ${order.followId})`
          copyMultipleLines(str, () => Toast('复制成功'))
        },
      }
      map[key] && map[key]()
    }

    // 提货单页面
    const isLandBill = computed(() => props.from == 'landBill')

    // 当前是否门店系统客户模块
    const isStore = computed(() => StoreController.isStore())

    const storeBusinessStatus = computed(() =>
      StoreController.StoreBusinessStatus()
    )

    // 后台是否开启核销按钮权限
    const isOpenVerifyButton = computed(() => {
      return store.getters.getAgentAuthList.some(
        item => item.permCode === AGRNT_AUTH_CODE.VERIFICATION_BUTTON
      )
    })

    // 是否展示全部核销按钮
    const canVerifyAll = computed(() => {
      const order = props.order
      return (
        storeBusinessStatus.value &&
        order.detailLs.length >= 1 &&
        order.remainVerifyNumber > 0 &&
        (order.isSupportVerifyAll || isLandBill.value) &&
        props.showVerify &&
        !isStore.value &&
        isOpenVerifyButton.value
      )
    })

    return {
      NP,
      merchandiseList,
      merchandiseNumber,
      merchandiseSpeciesNumber,
      isExpand,
      multipleMerchandiseList,
      description,
      onExtraClick,
      canVerifyAll,
      VERIFY_STATUS,
    }
  },
})
</script>

<style lang="scss" scoped>
.card-style-order {
  box-sizing: border-box;
  width: 100%;
  background: #fff;
  border-radius: 12px;
  margin-top: 10px;
  padding: 12px 16px;

  &__header {
    width: 100%;
    border-bottom: 1px solid #f5f7fa;
    padding-bottom: 12px;
  }

  &__main {
    position: relative;
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .description {
      display: flex;
      justify-content: space-between;
      span {
        display: inline-block;
        font-size: 14px;
        line-height: 22px;
      }

      .label {
        width: 80px;
        color: #919499;
        margin-right: 10px;
      }

      .value {
        color: #202124;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .suffix,
      .extra {
        color: #919499;
        font-style: normal;
      }
      .extra-tag {
        border-radius: 6px;
        margin-left: 10px;
        color: #ff4444;
        background-color: #fff1f1;
        display: inline-block;
        padding: 0 6px;
        font-size: 12px;
        font-weight: 400;
        line-height: 20px; /* 166.667% */
      }
    }
  }

  &__footer {
    padding: 12px 0 0;
    margin-top: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #f5f7fa;

    &__right {
      display: flex;
      gap: 12px;
    }

    .detail-btn {
      box-sizing: border-box;
      width: 80px;
      height: 32px;
      border-radius: 8px;
      padding: 6px 12px;
      border: 1px solid #f5f7fa;
      font-size: 13px;
      color: #202124;
      line-height: 20px;
      white-space: nowrap;
    }

    .verification-btn {
      background: #fff;
      color: #006cff;
      border-color: #006cff;
    }
  }
}

.has-verification {
  position: absolute;
  right: -12px;
  bottom: -12px;
  width: 66px;
  height: 58px;
}

.status {
  font-family: PingFangSC, PingFang SC;
  font-weight: 500;
  font-size: 12px;
  color: #919499;
  line-height: 12px;
  &.status--1 {
    color: #ffa800;
  }
  &.status--4 {
    color: #00b849;
  }
}

.single-merchandise {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.multiple-merchandise {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .expand-btn {
    color: #006cff;
    display: inline-block;
    min-width: 30px;
  }

  .summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #202124;
    line-height: 22px;
    font-size: 14px;

    span:first-of-type {
      em {
        color: #006cff;
        font-style: normal;
        padding: 0 4px;
      }
    }
  }
}

.merchandise-name {
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 14px;
  color: #202124;
  line-height: 22px;
  .name {
    display: inline-block;
    max-width: 170px;
    margin-right: 6px;
  }
  i {
    color: #919499;
    margin-left: 10px;
    font-style: normal;
    min-width: 18px;
  }
}

.pay-amount {
  line-height: 22px;
  font-size: 14px;
  color: #202124;
  font-weight: 400;

  em {
    font-style: normal;
    color: #ff4444 !important;
    margin-left: 2px;
  }
}
</style>
